/**
 * EXPORT BUTTON
 *
 * Dropdown button for exporting report data. Formats:
 *   - PDF  (server-rendered via OpenPDF)
 *   - Excel .xlsx  (server-rendered via Apache POI, multi-sheet)
 *   - CSV  (client-rendered)
 *   - CSV "Releases Summary" (client-rendered, project scope only)
 *
 * PDF + Excel hit the backend export endpoint which produces the bytes
 * server-side — the button just streams the response blob to a download.
 */

import React, { useCallback, useState } from 'react'
import Button from '@atlaskit/button'
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'
import DownloadIcon from '@atlaskit/icon/glyph/download'

import { fetchReportExport } from '../../../api/reportsApi'
import {
  generateReportCSV,
  generateReleasesSummaryCSV,
  downloadFile,
  downloadBlob,
  generateFilename
} from './exportUtils'

/**
 * Extract the scope id from the report payload. Each level's DTO puts the
 * id on a different field.
 */
const idFromReport = (level, reportData) => {
  if (!reportData) return null
  switch (level) {
    case 'project':
      return reportData.jiraProjectId
    case 'release':
      return reportData.releaseId
    case 'scenario':
      return reportData.scenarioId
    default:
      return null
  }
}

/**
 * Default filename for server-rendered downloads. The backend sets its own
 * Content-Disposition, but we pass a client-side default in case the browser
 * can't read the header (Safari in some iframe scenarios).
 */
const defaultServerFilename = (level, entityName, format) =>
  generateFilename(level, entityName, format === 'xlsx' ? 'xlsx' : 'pdf')

const ExportButton = ({
  reportData,
  level,
  entityName,
  releasesSummary,
  isDisabled = false,
  appearance = 'default'
}) => {
  const [busy, setBusy] = useState(false)

  // --- CSV (client-side) ---
  const handleExportCSV = useCallback(() => {
    if (!reportData) return
    const csv = generateReportCSV(reportData, level)
    const filename = generateFilename(level, entityName, 'csv')
    downloadFile(csv, filename)
  }, [reportData, level, entityName])

  const handleExportReleasesSummaryCSV = useCallback(() => {
    if (!releasesSummary || releasesSummary.length === 0) return
    const projectName = reportData?.projectName || entityName
    const csv = generateReleasesSummaryCSV(releasesSummary, projectName)
    const filename = generateFilename('releases_summary', projectName, 'csv')
    downloadFile(csv, filename)
  }, [releasesSummary, reportData, entityName])

  // --- Server-rendered PDF / Excel ---
  const handleServerExport = useCallback(
    async (format) => {
      const id = idFromReport(level, reportData)
      if (!id) return
      setBusy(true)
      try {
        const blob = await fetchReportExport(level, id, format)
        downloadBlob(blob, defaultServerFilename(level, entityName, format))
      } catch (err) {
        // Leave error reporting to the axios interceptor console output for now.
        console.error(`Export to ${format} failed`, err)
      } finally {
        setBusy(false)
      }
    },
    [level, reportData, entityName]
  )

  const hasData = reportData && reportData.summary
  const hasReleasesSummary = releasesSummary && releasesSummary.length > 0

  return (
    <DropdownMenu
      trigger={({ triggerRef, ...triggerProps }) => (
        <Button
          ref={triggerRef}
          {...triggerProps}
          iconBefore={<DownloadIcon label="" size="small" />}
          appearance={appearance}
          isDisabled={isDisabled || !hasData || busy}
          spacing="compact"
        >
          {busy ? 'Exporting…' : 'Export'}
        </Button>
      )}
      placement="bottom-end"
      shouldRenderToParent
    >
      <DropdownItemGroup>
        <DropdownItem
          onClick={() => handleServerExport('pdf')}
          isDisabled={!hasData || busy}
          description={`Export ${level} report as PDF`}
        >
          Export as PDF
        </DropdownItem>
        <DropdownItem
          onClick={() => handleServerExport('xlsx')}
          isDisabled={!hasData || busy}
          description={`Export ${level} report as Excel`}
        >
          Export as Excel (.xlsx)
        </DropdownItem>
        <DropdownItem
          onClick={handleExportCSV}
          isDisabled={!hasData}
          description={`Export ${level} report as CSV`}
        >
          Export as CSV
        </DropdownItem>

        {level === 'project' && hasReleasesSummary && (
          <DropdownItem
            onClick={handleExportReleasesSummaryCSV}
            description="Export all releases summary as CSV"
          >
            Releases Summary (CSV)
          </DropdownItem>
        )}
      </DropdownItemGroup>
    </DropdownMenu>
  )
}

export default ExportButton
